import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { CoinCard } from "@/pages/Explore";
import { ShieldCheck, Check, Rocket } from "lucide-react";

const Grid = ({ coins, testid }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testid}>
    {coins.map((c) => <CoinCard key={c.slug} c={c} />)}
  </div>
);

/** /verified — public directory of coins that pass every SolSpur trust check. */
export default function Verified() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/coins/verified").then((r) => setData(r.data)).catch(() => setData({ verified: [], almost: [], checks: [] }));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="verified-page">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00ff41] mb-3 flex items-center gap-2">
            <ShieldCheck size={12} /> // SolSpur Verified
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-4">
            Buy safer.<br /><span className="text-[#00ff41]">Every check passed.</span>
          </h1>
          <p className="text-[#e2e2e2] max-w-2xl mb-10 text-base">
            These coins passed all seven SolSpur trust checks on-chain: supply is capped forever, wallets can never be frozen,
            liquidity is live and verified, and the creator has a whitepaper and a linked X account. Re-verified daily.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-14" data-testid="verified-checks">
            {(data?.checks || []).map((ch) => (
              <div key={ch.id} className="border border-[#3a3a3a] bg-[#121212] p-4 flex gap-3">
                <span className="mt-0.5 w-4 h-4 flex-shrink-0 flex items-center justify-center border border-[#00ff41] text-[#00ff41]"><Check size={10} strokeWidth={3} /></span>
                <div>
                  <div className="font-mono text-xs text-white">{ch.label}</div>
                  <div className="text-[11px] text-[#c4c4c4] mt-1">{ch.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {data === null ? (
            <div className="h-40 border border-[#3a3a3a] animate-pulse" />
          ) : data.verified.length > 0 ? (
            <>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41] mb-4" data-testid="verified-count">
                {data.verified.length} verified coin{data.verified.length === 1 ? "" : "s"}
              </div>
              <Grid coins={data.verified} testid="verified-grid" />
            </>
          ) : (
            <div className="border border-[#00ff41]/40 bg-[#121212] p-10 text-center" data-testid="verified-empty">
              <ShieldCheck size={36} className="mx-auto text-[#00ff41] mb-4" />
              <h2 className="font-display font-black uppercase tracking-tight text-lg md:text-lg mb-2">
                No coin has passed all seven checks yet.
              </h2>
              <p className="text-[#e2e2e2] text-sm max-w-md mx-auto mb-6">
                The first creator to mint, revoke authorities, open a Raydium pool and link their X earns the badge — and the
                top spot on this page for as long as they hold it.
              </p>
              <Link to="/create" data-testid="verified-cta" className="btn-brutal inline-flex bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4 items-center gap-2">
                <Rocket size={16} /> Be the first
              </Link>
            </div>
          )}

          {data?.almost?.length > 0 && (
            <section className="mt-16">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-1">Almost there</div>
              <p className="text-[#e2e2e2] text-sm mb-5">Trust score 60+ but missing a check — tap in to see what's left.</p>
              <Grid coins={data.almost} testid="almost-grid" />
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
